import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, throwError, map, of, firstValueFrom } from 'rxjs';
import { NavigationExtras, Router } from '@angular/router';
import { UserProjectPermissions } from '../model/interfaces/user-project-permissions.interface';
import { AuthResponseGetProjectUserPermissions } from '../model/interfaces/auth-response-get-project-user-permissions.interface';
import { LocalStorageService } from '@shared/service/local-storage/local-storage.service';
import { LanguageService } from '@shared/service/language/language.service';
import { SystemService } from '@shared/service/system/system.service';
import { Language } from '@shared/model/interfaces/language.interface';
import { LocalStorageKeys } from '../model/enums/local-storage.keys';
import { environment } from '@env/environment';
import { AuthPayloadChangePassword } from '../model/interfaces/auth-payload-change-password.interface';
import { AuthPayloadMfaData } from '../model/interfaces/auth-payload-mfa-data.interface';
import { AuthPayloadMfaResendCode } from '../model/interfaces/auth-payload-mfa-code-resend.interface';
import { AuthPayloadMfaVerifyCode } from '../model/interfaces/auth-payload-mfa-verivy-code.interface';
import { AuthPayloadResetPassword } from '../model/interfaces/auth-payload-reset-password.interface';
import { AuthResponseMfaData } from '../model/interfaces/auth-response-mfa-data.interface';
import { AuthResponseMfaResendCode } from '../model/interfaces/auth-response-mfa-resend-code.interface';
import { AuthResponseChangePassword } from '../model/interfaces/auth-response-change-password.interface';
import { AuthFlowComponent } from '../model/types/auth-flow-component.type';
import { AuthResponseMfaVerifyCode } from '../model/interfaces/auth-response-mfa-verify-code.interface';
import { ActionState } from '../model/enums/auth-state.enum';
import { AuthMessage } from '../model/interfaces/auth-message.interface';
import { Messages } from '../model/data/auth-messages';
import { PermissionSource } from '@shared/model/enums/permission-source.enum';
import {
  isArrayOfStrings,
  toCamelCase,
  toSnakeCase,
} from '@shared/utils/string-case-manipulation';
import { getCookie } from '@shared/utils/cookie';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { AuthMessageResponse } from '../model/interfaces/auth-message-response.interface';
import { ControlResendCodeComponent } from '../shared/component/control-resend-code/control-resend-code.component';
import { ResetPasswordComponent } from '../component/reset-password/reset-password.component';
import { ChangePasswordComponent } from '../component/change-password/change-password.component';
import { LoginComponent } from '../component/login/login.component';
import { MfaComponent } from '../component/mfa/mfa.component';
import { ForgotPasswordComponent } from '../component/forgot-password/forgot-password.component';
import { User } from '../model/interfaces/user.interface';
import { navItemsProperties } from '../../layout/left-panel/_navigation-items';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private systemService = inject(SystemService);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  localStorageService = inject(LocalStorageService);
  private translate = inject(TranslateService);
  private http = inject(HttpClient);

  private _username = '';
  private _password = '';
  private _user$ = new BehaviorSubject<User | null>(null);
  public readonly user$ = this._user$.asObservable();
  public mfaType: string | null = null;

  private _userPermissionsSubject = new BehaviorSubject<Record<string, UserProjectPermissions>>({});
  public userPermissionsSignal = signal<Record<string, UserProjectPermissions>>({});

  private _projectId = new BehaviorSubject<string | null>(null);
  private _projectName = new BehaviorSubject<string | null>(null);

  readonly messages: AuthMessage[] = Messages;
  mfaOptions!: AuthResponseMfaData;
  language: Language;
  animationDuration = { value: '', params: { duration: '600ms' } };

  blockTimeLeft?: string;
  private blockTimer = signal('00:00');
  private blockInterval?: ReturnType<typeof setInterval>;

  constructor() {
    const languageService = this.languageService;

    this.language = languageService.getLanguage();
    this._userPermissionsSubject.subscribe((value) => {
      this.userPermissionsSignal.set(value);
    });
  }

  get userPermissions(): Record<string, UserProjectPermissions> {
    return this.userPermissionsSignal();
  }

  get username(): string {
    return this._username;
  }

  set username(value: string) {
    this._username = value;
  }

  get password(): string {
    return this._password;
  }

  set password(value: string) {
    this._password = value;
  }

  get user(): User | null {
    return this._user$.value;
  }

  set user(user: User) {
    this._user$.next(user);
  }

  deleteUser() {
    this._user$.next(null);
  }

  get projectId(): string | null {
    return this._projectId.value;
  }

  set projectId(value: string) {
    if (!value) {
      return;
    }

    this._projectId.next(value);
    this.localStorageService.setItem(LocalStorageKeys.SELECTED_PROJECT_ID, String(value));
  }

  get projectName(): string | null {
    return this._projectName.value;
  }

  set projectName(value: string) {
    if (!value) {
      return;
    }

    this._projectName.next(value);
    this.localStorageService.setItem(LocalStorageKeys.SELECTED_PROJECT_NAME, value);
  }

  public async validateProjectUserPermissions(
    projectName: string,
  ): Promise<void> {
    if (!this.getProjectUserPermissions(projectName)) {
      const result: AuthResponseGetProjectUserPermissions =
        await this.fetchProjectUserPermissions(projectName);
      this.setProjectUserPermissions(
        projectName,
        toCamelCase(result.data['permissions']) as UserProjectPermissions,
      );
    }
  }

  getProjectUserPermissions(
    projectName: string,
  ): UserProjectPermissions | undefined {
    return this._userPermissionsSubject.getValue()[projectName];
  }

  getCurrentProjectUserPermissions(): UserProjectPermissions | undefined {
    const project = this.systemService.getSelectedProject();
    if (!project) {
      return undefined;
    }
    return this._userPermissionsSubject.getValue()[project];
  }

  getPermissionsContext(): Record<PermissionSource, unknown> {
    return {
      [PermissionSource.SYSTEM_SETTINGS]: this.systemService.systemSettings,
      [PermissionSource.USER_DATA]: this.user,
      [PermissionSource.USER_PROJECT_PERMISSIONS]:
        this.getCurrentProjectUserPermissions(),
    };
  }

  getRoutePermissions(routeUrl: string): boolean {
    const navigationItem = navItemsProperties
      .map((item) => {
        let found;
        if (item.children) {
          found = item.children.find((item) => item.url === routeUrl);
        } else if (item.url === routeUrl) {
          found = item;
        }
        return found;
      }).find(it => !!it);

    if (!navigationItem || !navigationItem.permission) return false;

    const context = this.getPermissionsContext();

    return navigationItem.permission.some(({ source, key }) => {
      const sourceContext = context[source as PermissionSource];

      if (!sourceContext || typeof sourceContext !== 'object') return false;

      const record = sourceContext as Record<string, unknown>;
      const value = record[key];

      return Boolean(value);
    });
  }

  setProjectUserPermissions(
    projectName: string,
    permissions: UserProjectPermissions,
  ): void {
    const current = this._userPermissionsSubject.getValue();
    const updated = { ...current, [projectName]: permissions };
    this._userPermissionsSubject.next(updated);
  }

  navigateTo(url: string, extras: NavigationExtras = {}): Promise<boolean> {
    // CRITICAL: Handle login/logout routes including those with project names
    // e.g., /login, /login/app, /logout, /logout/app
    if (url.startsWith('/login') || url.startsWith('/logout')) {
      window.location.href = url;
      return Promise.resolve(false);
    }

    const urlPath = url.split('?')[0].split('#')[0]; // Remove query params and hash

    // Routes that are handled by nginx (external services)
    // These need full page navigation, not Angular router navigation
    const nginxHandledPatterns = [
      /\/tasks$/,  // Routes ending with /tasks (e.g., /project/tasks)
    ];

    const isNginxRoute = nginxHandledPatterns.some(pattern => pattern.test(urlPath));

    if (isNginxRoute) {
      // Full page navigation - let nginx handle the routing
      window.location.href = url;
      return Promise.resolve(false);
    }

    // Check if this is an Angular route by checking known route prefixes
    const angularRoutePrefixes = [
      '/dashboard',
      '/sessions',
      '/companies',
      '/users',
      '/projects',
      '/integrations',
      '/analytics',
      '/templates',
      '/workflows2',
      '/processes',
      '/post-transactions',
      '/tariffs',
      '/audit-logs',
      '/dictionaries',
      '/categories',
      '/texts-tables',
      '/service-call',
      '/change-password',
      '/forgot-password',
      '/mfa',
      '/reset-password',
      '/app/', // Angular base href
    ];

    const isAngularRoute = angularRoutePrefixes.some(prefix => urlPath.startsWith(prefix));

    if (!isAngularRoute) {
      // Not an Angular route - use full page navigation (probably old AngularJS app route)
      window.location.href = url;
      return Promise.resolve(false);
    }

    return this.router.navigateByUrl(url, extras);
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  login(username: string, password: string): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(`${environment.API_URL}auth/login`, { username, password }).pipe(
      catchError((error) => {
        return throwError(() => this.normalizeError(error.error));
      })
    );
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkProject(projectName: string, nextUrl: string): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(`${environment.API_URL}auth/login`, {
      project: projectName,
      next_url: `${environment.APP_URL}${nextUrl}`,
    }).pipe(
      map((response) => {
        const data = response.data ?? response;
        if (data.authentication === 'saml2' && data.redirect) {
          // CRITICAL: Store redirect URL in localStorage before SAML redirect
          // This ensures the redirect URL survives the SSO flow even if
          // RelayState is not preserved by the IdP/backend
          if (nextUrl) {
            this.localStorageService.setItem(LocalStorageKeys.SSO_REDIRECT_URL, nextUrl);
          }
          window.location.href = data.redirect;
        }
        return data;
      }),
      catchError((error: HttpErrorResponse) => {
        // Like old manager-app: return local auth on any error (including CSRF)
        // This prevents endless loops in SSO flows
        if (error.status === 0) {
          return of({ authentication: 'local' });
        }
        return throwError(() => this.normalizeError(error.error));
      })
    );
  }

  /**
   * Retrieves and clears the SSO redirect URL stored before SAML authentication.
   * Used to redirect the user to their intended destination after SSO completes.
   */
  getAndClearSsoRedirectUrl(): string | null {
    const redirectUrl = this.localStorageService.getItem(LocalStorageKeys.SSO_REDIRECT_URL);
    if (redirectUrl) {
      this.localStorageService.removeItem(LocalStorageKeys.SSO_REDIRECT_URL);
    }
    return redirectUrl;
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async logout(): Promise<any> {
    try {
      return await firstValueFrom(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.http.post<any>(`${environment.API_URL}auth/logout`, {})
      );
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async fetchProjectUserPermissions(
    projectName: string,
  ): Promise<AuthResponseGetProjectUserPermissions> {
    const url = `${environment.API_URL}projects/${projectName}/user/permissions/`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`fetchProjectUserPermissions failed: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forgotPassword(email: string): Observable<any> {
    return new Observable((observer) => {
      fetch(`${environment.API_URL}auth/reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) {
            observer.error(this.normalizeError(data));
            return;
          }
          observer.next(data);
          observer.complete();
        })
        .catch((error) => observer.error({ error }));
    });
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changePassword(data: AuthPayloadChangePassword): Observable<any> {
    return new Observable((observer) => {
      const csrfToken = getCookie('csrftoken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      fetch(`${environment.API_URL}auth/change_password`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(toSnakeCase(data)),
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) {
            observer.error(this.normalizeError(data));
            return;
          }
          observer.next(data);
          observer.complete();
        })
        .catch((error) => observer.error({ error }));
    });
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resetPassword(data: AuthPayloadResetPassword): Observable<any> {
    const params = new URLSearchParams();
    params.append('new_password1', data.newPassword1);
    params.append('new_password2', data.newPassword2);
    params.append('uidb64', data.uidb64);
    params.append('token', data.token);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(`${environment.API_URL}auth/reset/${data.uidb64}/${data.token}/`, params.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(
      catchError((error) => {
        const rawError = error.error?.error ?? error.error;
        const normalizedErrors = Array.isArray(rawError)
          ? rawError.map(this.parseErrorDetail.bind(this))
          : [this.parseErrorDetail(rawError)];
        return throwError(() => normalizedErrors);
      })
    );
  }

  private parseErrorDetail(error: string): string {
    if (typeof error !== 'string') {
      return String(error);
    }

    // Extract error code from Django ErrorDetail format
    const match = error.match(/string='([^']+)'/);
    let errorCode = match?.[1] || error;

    // Normalize backend error codes to match Messages array
    if (errorCode === 'password_mismatch') {
      errorCode = 'mismatch';
    }

    return errorCode;
  }

  normalizeError = <T>(data: T | { error: T }): T => {
    const error = typeof data === 'object' && data !== null && 'error' in data
      ? data.error
      : data;

    if (typeof error === 'object' && error !== null && 'detail' in error) {
      const detail = (error as { detail: unknown }).detail;
      // Convert detail to code format expected by login component
      return { code: String(detail) } as T;
    }

    return error as T;
  };

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async isLoggedIn(projectName: string): Promise<any> {
    const parameters = projectName ? `?project=${projectName}` : '';
    try {
      const response = await fetch(`${environment.API_URL}me${parameters}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        return Promise.reject(new Error('Failed to fetch user data'));
      }
      return await response.json();
    } catch (error: unknown) {
      return Promise.reject(error);
    }
  }

  isAuthenticated(): boolean {
    return !!this._user$.value;
  }

  async doLogout(): Promise<void> {
    await this.logout();
    this.deleteUser();
    this.navigateTo('/logout');
  }

  getMfaOptions(data: AuthPayloadMfaData): Promise<AuthResponseMfaData> {
    return fetch(`${environment.API_URL}mfa-login/login_get_options/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .catch((error) => Promise.reject(error));
  }

  async initMfaOptions(component: ControlResendCodeComponent): Promise<void> {
    try {
      const param: AuthPayloadMfaData = {
        username: this.username,
        password: this.password,
        project: this.projectId,
      };
      this.mfaOptions = await this.getMfaOptions(param);
    } catch {
      this.finalizeAction(component, ActionState.FAILURE, ['']);
    }
  }

  authMfaResendCode(data: AuthPayloadMfaResendCode): Promise<AuthResponseMfaResendCode> {
    return fetch(`${environment.API_URL}mfa-login/login_resend_code/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(async (response) => {
      // Check content type to avoid JSON parse errors on HTML error pages
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          throw new Error('server_error');
        }
        throw new Error('invalid_response');
      }

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData?.error?.message || 'server_error');
      }
      return responseData;
    });
  }

  async authMfaVerifyCode(
    data: AuthPayloadMfaVerifyCode,
  ): Promise<AuthResponseMfaVerifyCode> {
    return fetch(`${environment.API_URL}mfa-login/login_verify_code/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(async (response): Promise<AuthResponseMfaVerifyCode> => {
      if (!response.ok) {
        return await response.json().then((errorData) => {
          const errorMessage =
            errorData?.err || errorData?.error?.message || '';
          throw isArrayOfStrings(errorMessage) ? errorMessage : [errorMessage];
        });
      }
      return response.json();
    });
  }

  validatorPasswordMatch: ValidatorFn = (control: AbstractControl) => {
    const formGroup = control as FormGroup;
    const password1 = formGroup.get('newPassword1')?.value;
    const password2 = formGroup.get('newPassword2')?.value;
    const touched = formGroup.get('newPassword2')?.touched;

    if (password1 === password2 || !touched) {
      return null; // Passwords match
    }

    if (password1.length === password2.length) {
      return { mismatch: true }; // Passwords are of equal length but different
    }

    return { length_mismatch: true }; // Passwords are not match
  };

  validateInput(component: ResetPasswordComponent | ChangePasswordComponent) {
    if (component.form.dirty) {
      component.messages = Object.entries(component.form.errors || {})
        .filter(([_key, value]) => value) // Only include entries where there is an error
        .map(([key, _value]) => ({
          code: key,
          type: ActionState.FAILURE,
        }));
    } else {
      component.messages = [];
    }
  }

  setState(
    component:
      | ChangePasswordComponent
      | LoginComponent
      | MfaComponent
      | ResetPasswordComponent
      | ForgotPasswordComponent
      | ControlResendCodeComponent,
    state: ActionState,
  ): void {
    component.state = state;
  }

  onPasswordChange(
    component: ChangePasswordComponent | ResetPasswordComponent,
  ) {
    this.resetMessages(component);
    this.validateInput(component);
  }

  handleFormAction(
    component:
      | ChangePasswordComponent
      | LoginComponent
      | MfaComponent
      | ResetPasswordComponent
      | ControlResendCodeComponent,
  ): void {
    component.form.markAsPristine();
    component.form.markAsUntouched();
    this.setState(component, ActionState.IN_PROGRESS);
    this.resetMessages(component);
  }

  actionInProgress(component: AuthFlowComponent): boolean {
    return component.state === ActionState.IN_PROGRESS;
  }

  actionFailed(component: LoginComponent): boolean {
    return component.state == ActionState.FAILURE;
  }

  actionSucceeded(component: AuthFlowComponent): boolean {
    return component.state == ActionState.SUCCESS;
  }

  getMessage(errorCode: AuthMessageResponse): string {
    const error = this.messages.find(
      (msg) => msg.code === errorCode.code?.toLowerCase(),
    );
    return (
      error?.message ||
      error?.messageKey ||
      this.messages.find((msg) => msg.code === '-default-message')
        ?.messageKey ||
      ''
    );
  }

  hasError(
    component:
      | ChangePasswordComponent
      | MfaComponent
      | ResetPasswordComponent
      | LoginComponent
      | ControlResendCodeComponent,
  ): boolean {
    return component.messages.some((msg) => msg.type === ActionState.FAILURE);
  }

  // TODO: specify return type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showAlertMessage(component: any): boolean {
    return component.messages.length > 0;
  }

  resetMessages(
    component:
      | LoginComponent
      | ChangePasswordComponent
      | ControlResendCodeComponent
      | MfaComponent
      | ResetPasswordComponent,
  ): void {
    component.messages = [];
  }

  formHasErrors(component: AuthFlowComponent): boolean {
    return (
      component.form.invalid ||
      component.messages.some((message) => message.type === ActionState.FAILURE)
    );
  }

  submitButtonIsDisabled(component: AuthFlowComponent): boolean {
    return component?.form?.invalid || this.actionInProgress(component);
  }

  getAlertType(
    component:
      | LoginComponent
      | ChangePasswordComponent
      | MfaComponent
      | ResetPasswordComponent
      | ControlResendCodeComponent
      | ForgotPasswordComponent,
  ): ActionState {
    return component.messages.length === 0 ||
      component.messages.some((message) => message.type === ActionState.FAILURE)
      ? ActionState.FAILURE
      : ActionState.SUCCESS;
  }

  finalizeAction(
    component:
      | LoginComponent
      | ChangePasswordComponent
      | MfaComponent
      | ResetPasswordComponent
      | ControlResendCodeComponent
      | ForgotPasswordComponent,
    state: ActionState,
    errors: AuthResponseChangePassword[] | string[],
  ): void {
    this.setState(component, state);

    component.messages = errors.map((error) => ({
      code: String(error),
      type: state,
    }));

    if (component instanceof LoginComponent && this.blockTimeLeft) {
      this.startBlockCountdown(new Date(this.blockTimeLeft));
    }
  }

  startBlockCountdown(blockTimeLeft: Date) {
    this.stopBlockCountdown();
    this.updateTimer(blockTimeLeft);
    this.blockInterval = setInterval(
      () => this.updateTimer(blockTimeLeft),
      1000,
    );
  }

  stopBlockCountdown() {
    if (this.blockInterval) {
      clearInterval(this.blockInterval);
      this.blockInterval = undefined;
    }
  }

  formatTemporaryBlockMessage(): void {
    const error = this.messages.find(
      (msg) => msg.code === 'auth_login_temporary',
    );
    if (error) {
      error.message = this.translate
        .instant('AUTH_LOGIN_ERROR_BLOCKED_TEMPORARY')
        .replace('$minutes', this.blockTimer());
    }
  }

  private updateTimer(blockTimeLeft: Date) {
    const now = new Date();
    const difference = Math.max(
      0,
      Math.floor((blockTimeLeft.getTime() - now.getTime()) / 1000),
    );
    const minutes = String(Math.floor(difference / 60)).padStart(2, '0');
    const seconds = String(difference % 60).padStart(2, '0');
    this.blockTimer.set(`${minutes}:${seconds}`);

    this.formatTemporaryBlockMessage();

    if (difference <= 0) {
      this.stopBlockCountdown();
      // CRITICAL: Navigate to login page instead of location.reload()
      // location.reload() can cause navigation to invalid URLs like /app/login/project
      const loginUrl = `/login${this.projectName ? '/' + this.projectName : ''}`;
      this.navigateTo(loginUrl);
    }
  }

  sendVerificationCode(
    payload: object,
    callback: (data?: Record<string, unknown>, error?: HttpErrorResponse) => void
  ) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    this.http
      .post<Record<string, unknown>>(
        '/api/internal/mfa-login/send_verification_code/',
        payload,
        httpOptions
      )
      .subscribe({
        next: (data) => callback(data),
        error: (err) => callback(undefined, err)
      });
  }

  verifyCode(
    payload: object,
    callback: (data?: Record<string, unknown>, error?: HttpErrorResponse) => void
  ) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    this.http
      .post<Record<string, unknown>>(
        '/api/internal/mfa-login/verify_code/',
        payload,
        httpOptions
      )
      .subscribe({
        next: (data) => callback(data),
        error: (err) => callback(undefined, err)
      });
  }
}
